<template>
  <Modal
    v-if="!showExtraConfirmation"
    :is-open="elementToDelete !== undefined"
    :title="title"
    :header-color="CONSTANTS.DIALOGS_HEADER_color"
    :card-actions-enabled="true"
    :disable-close="isDeletingElement"
    @close-dialog="cancelDeletion"
  >
    <template #body>
      <v-row class="justify-center">
        <v-col class="col" cols="2">
          <v-icon class="icon-position" color="error" size="50">
            {{ CONSTANTS.ICONS_MDI.ICON_ALERT_CIRCLE_OUTLINE }}
          </v-icon>
        </v-col>
        <v-col cols="10" class="pl-3 col">
          <p>{{ descriptionBeforeProceed }}</p>
        </v-col>
      </v-row>
    </template>
    <template #btn-group>
      <v-row class="justify-center btn-container">
        <v-col>
          <v-btn
            v-if="extraConfirmationRequired"
            :loading="isDeletingElement"
            :disabled="isDeletingElement"
            class="bw-button centered-button"
            variant="flat"
            :prepend-icon="CONSTANTS.ICONS_MDI.ICON_DELETE"
            @click="proceedToExtraConfirmation"
          >
            {{ labels.BUTTONS.PROCEED }}
          </v-btn>

          <v-btn
            v-else
            :loading="isDeletingElement"
            :disabled="isDeletingElement"
            class="bw-button centered-button"
            variant="flat"
            :prepend-icon="CONSTANTS.ICONS_MDI.ICON_DELETE"
            @click="confirmDeletion"
          >
            {{ deleteButtonLabel }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </Modal>

  <Modal
    v-if="showExtraConfirmation"
    :title="title"
    :is-open="elementToDelete !== undefined"
    :header-color="CONSTANTS.DIALOGS_HEADER_color"
    :card-actions-enabled="false"
    width="800px"
    :disable-close="isDeletingElement"
    @close-dialog="cancelDeletion"
  >
    <template #body>
      <v-container class="pl-0 pr-0">
        <v-row class="justify-center">
          <v-col class="col" cols="2">
            <v-icon class="icon-position" color="error" size="50">
              {{ CONSTANTS.ICONS_MDI.ICON_ALERT_CIRCLE_OUTLINE }}
            </v-icon>
          </v-col>
          <v-col class="col" cols="10">
            {{ descriptionAfterProceed }}
          </v-col>
        </v-row>

        <v-form @submit.prevent="confirmDeletion">
          <v-row class="justify-center">
            <v-col class="col" cols="12">
              <v-text-field
                id="inputField"
                data-testid="confirm-delete-field"
                input
                variant="outlined"
                autocomplete="off"
                :rules="[checkExtraConfirmation]"
              />
            </v-col>
          </v-row>

          <v-row class="justify-center">
            <v-col cols="12">
              <v-btn
                :loading="isDeletingElement"
                color="error"
                class="text-white delete-btn"
                width="800"
                height="33"
                :disabled="!extraConfirmationPassed || isDeletingElement"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_DELETE_EMPTY"
                type="submit"
              >
                {{ deleteButtonLabel }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-row class="justify-center mt-8">
          <v-col cols="2">
            <v-icon class="pr-1 pb-6" size="2em">
              {{ CONSTANTS.ICONS_MDI.ICON_INFORMATION_OUTLINE }}
            </v-icon>
          </v-col>
          <v-col class="pl-0 ml-0 col" cols="10">
            <p class="pt-2 final-warning">{{ descriptionFinalWarning }}</p>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </Modal>
</template>

<!-- T is an object, especially not undefined or null -->
<script setup lang="ts" generic="T extends Object">
  import * as HELPERS from '@/utils/helpers'
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import Modal from '@/components/Modal/Modal.vue'
  import { computed, ref } from 'vue'

  interface Props<T> {
    elementToDelete: T | undefined
    getElementName: (t: T | undefined) => string
    elementTypeName: string
    isDeletingElement: boolean
    extraConfirmationRequired?: boolean
  }

  const props = withDefaults(defineProps<Props<T>>(), {
    getElementName: (_element: T | undefined) => 'this element',
    isDeletingElement: false,
    extraConfirmationRequired: false,
  })

  const emit = defineEmits<{
    (event: 'confirmDeletion', value: T): void
    (event: 'cancelDeletion'): void
  }>()

  /*
   * component state
   */
  const showExtraConfirmation = ref(false)
  const extraConfirmationPassed = ref(false)

  /*
   * computed
   */
  const title = computed(() =>
    HELPERS.replaceVariableInString(labels.DELETE_ELEMENT.TITLE, props.elementTypeName),
  )

  const descriptionBeforeProceed = computed(() =>
    HELPERS.replaceVariableInString(
      labels.DELETE_ELEMENT.DESCRIPTION_BEFORE_PROCEED,
      props.getElementName(props.elementToDelete),
    ),
  )

  const descriptionAfterProceed = computed(() =>
    HELPERS.replaceVariableInString(
      labels.DELETE_ELEMENT.DESCRIPTION_AFTER_PROCEED,
      props.getElementName(props.elementToDelete),
    ),
  )

  const descriptionFinalWarning = computed(() =>
    HELPERS.replaceVariableInString(
      labels.DELETE_ELEMENT.DESCRIPTION_FINAL_WARNING,
      props.getElementName(props.elementToDelete),
    ),
  )

  const deleteButtonLabel = computed(() =>
    HELPERS.replaceVariableInString(labels.DELETE_ELEMENT.DELETE_BUTTON, props.elementTypeName),
  )

  /*
   * methods
   */
  function proceedToExtraConfirmation() {
    showExtraConfirmation.value = true
  }

  function checkExtraConfirmation(value: string): boolean {
    extraConfirmationPassed.value = value === 'DELETE'

    return extraConfirmationPassed.value
  }

  function confirmDeletion(): void {
    if (props.elementToDelete) {
      emit('confirmDeletion', props.elementToDelete)
    }
  }

  function cancelDeletion(): void {
    emit('cancelDeletion')
  }
</script>

<style lang="scss" scoped>
  .bw-button {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .btn-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .centered-button {
    width: 100%;
  }

  .cancel-button {
    background-color: rgb(var(--v-theme-grey-light));
    color: rgb(var(--v-theme-font-color));
  }

  .icon-position {
    padding-bottom: 7px;
    padding-right: 5px;
  }

  .final-warning {
    font-size: 0.75rem;
    text-align: justify;
  }
</style>
